/**
 * CareLogCard Component
 *
 * Displays a single care log entry as a card with metadata, AI extracted data,
 * and quick actions.
 */

import { Edit2, Calendar, Clock, AlertTriangle, Loader2, AlertCircle } from 'lucide-react';

// Processing status for AI analysis
type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Severity levels
type SeverityLevel = 'routine' | 'notable' | 'concerning' | 'urgent';

// Sentiment types
type Sentiment = 'positive' | 'neutral' | 'negative' | 'concerning';

// Extracted behavior
interface ExtractedBehavior {
  behavior: string;
  count?: number;
  intensity?: 'mild' | 'moderate' | 'severe';
}

// Extracted data from AI
interface ExtractedData {
  behaviors?: ExtractedBehavior[];
  mood?: string;
  medicationsTaken?: Array<{ medicationName: string }>;
  medicationsRefused?: string[];
  symptoms?: Array<{ symptom: string; severity?: string }>;
  incidents?: Array<{ type: string; description: string }>;
}

export interface CareLogSummary {
  logId: string;
  caseFileId: string;
  logText: string;
  logDate: string;
  logTimestamp: { _seconds: number; _nanoseconds: number };
  tags: string[];
  inputType: 'text' | 'voice';
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  // Phase 2D: AI processing fields
  processingStatus?: ProcessingStatus;
  extractedData?: ExtractedData;
  sentiment?: Sentiment;
  severityLevel?: SeverityLevel;
  summary?: string;
  flagsForReview?: boolean;
}

interface CareLogCardProps {
  careLog: CareLogSummary;
  onClick?: () => void;
  onEdit?: (logId: string) => void;
}

const TAG_COLORS: Record<string, string> = {
  medical: 'bg-red-100 text-red-800',
  school: 'bg-blue-100 text-blue-800',
  insurance: 'bg-green-100 text-green-800',
  admin: 'bg-gray-100 text-gray-800',
  behavior: 'bg-purple-100 text-purple-800',
  medication: 'bg-orange-100 text-orange-800',
};

const SEVERITY_STYLES: Record<SeverityLevel, { bg: string; text: string; border: string }> = {
  routine: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  notable: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  concerning: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  urgent: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const SENTIMENT_ICONS: Record<Sentiment, string> = {
  positive: '😊',
  neutral: '😐',
  negative: '😟',
  concerning: '⚠️',
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(timestamp: { _seconds: number }): string {
  if (!timestamp?._seconds) return '';
  const date = new Date(timestamp._seconds * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export function CareLogCard({
  careLog,
  onClick,
  onEdit,
}: CareLogCardProps): JSX.Element {
  const severityStyle = careLog.severityLevel ? SEVERITY_STYLES[careLog.severityLevel] : null;
  const showSeverityBorder = careLog.severityLevel && careLog.severityLevel !== 'routine';

  return (
    <div
      className={`card group hover:shadow-card-hover transition-all ${
        onClick ? 'cursor-pointer' : ''
      } ${showSeverityBorder ? `border-l-4 ${severityStyle?.border}` : ''}`}
      onClick={onClick}
    >
      <div className="card-body">
        {/* Header with date, time, and severity */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3 text-sm text-muted">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {formatDate(careLog.logDate)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {formatTime(careLog.createdAt)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Severity badge */}
            {careLog.severityLevel && careLog.severityLevel !== 'routine' && severityStyle && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityStyle.bg} ${severityStyle.text}`}>
                {careLog.severityLevel}
              </span>
            )}
            {/* Sentiment indicator */}
            {careLog.sentiment && (
              <span className="text-sm" title={`Sentiment: ${careLog.sentiment}`}>
                {SENTIMENT_ICONS[careLog.sentiment]}
              </span>
            )}
            {/* Processing status indicator */}
            {careLog.processingStatus === 'processing' && (
              <span title="AI processing...">
                <Loader2 size={14} className="animate-spin text-muted" />
              </span>
            )}
            {careLog.processingStatus === 'failed' && (
              <span title="AI processing failed">
                <AlertCircle size={14} className="text-red-500" />
              </span>
            )}
            {/* Flagged for review */}
            {careLog.flagsForReview && (
              <span title="Flagged for review">
                <AlertTriangle size={14} className="text-orange-500" />
              </span>
            )}
            {/* Voice badge */}
            {careLog.inputType === 'voice' && (
              <span className="badge badge-secondary text-xs">Voice</span>
            )}
          </div>
        </div>

        {/* AI Summary (if available) */}
        {careLog.summary && (
          <p className="text-sm text-ink font-medium mb-2 bg-stone/30 px-2 py-1 rounded">
            {careLog.summary}
          </p>
        )}

        {/* Log text preview */}
        <p className="text-ink mb-3 whitespace-pre-wrap">
          {truncateText(careLog.logText, 200)}
        </p>

        {/* AI Extracted Data Pills */}
        {careLog.extractedData && careLog.processingStatus === 'completed' && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {/* Behaviors */}
            {careLog.extractedData.behaviors?.map((b, i) => (
              <span
                key={`behavior-${i}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
              >
                {b.behavior}{b.count && b.count > 1 ? ` (${b.count}x)` : ''}
              </span>
            ))}
            {/* Medications taken */}
            {careLog.extractedData.medicationsTaken?.map((m, i) => (
              <span
                key={`med-${i}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {m.medicationName}
              </span>
            ))}
            {/* Medications refused */}
            {careLog.extractedData.medicationsRefused?.map((m, i) => (
              <span
                key={`refused-${i}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
              >
                {m} (refused)
              </span>
            ))}
            {/* Symptoms */}
            {careLog.extractedData.symptoms?.map((s, i) => (
              <span
                key={`symptom-${i}`}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
              >
                {s.symptom}
              </span>
            ))}
            {/* Mood */}
            {careLog.extractedData.mood && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Mood: {careLog.extractedData.mood}
              </span>
            )}
          </div>
        )}

        {/* User-applied Tags */}
        {careLog.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {careLog.tags.map((tag) => (
              <span
                key={tag}
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  TAG_COLORS[tag] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions - visible on hover */}
        <div className="pt-2 border-t border-soft-neutral opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(careLog.logId);
              }}
              className="btn-ghost btn-sm flex items-center gap-1"
            >
              <Edit2 size={14} />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CareLogCard;
